import React, { useEffect, useState } from 'react';
import { db, functions } from '../../config/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import './styles.css';

export const AdminMetrics = () => {
  const [totalSelections, setTotalSelections] = useState(null);
  const [topSongs, setTopSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState(null);

  // Fetch total selections count
  useEffect(() => {
    const fetchTotalSelections = async () => {
      try {
        const getTotalSelections = httpsCallable(functions, 'getTotalSelections');
        console.log('Calling getTotalSelections function...');
        const result = await getTotalSelections();
        console.log('Function response:', result);
        setTotalSelections(result.data.total);
        setError(null);
      } catch (err) {
        console.error('Error details:', {
          message: err.message,
          code: err.code,
          details: err.details,
          stack: err.stack,
        });
        setError(err.message || 'An error occurred while fetching total selections');
      } finally {
        setLoading(false);
      }
    };

    fetchTotalSelections();
  }, []);

  // Subscribe to real-time songCounts for all songs
  useEffect(() => {
    console.log('Setting up songCounts listener...');
    const songCountsRef = collection(db, 'songCounts');
    const allSongsQuery = query(
      songCountsRef,
      orderBy('count', 'desc')
      // Removed limit - fetch all songs
    );

    const unsubscribe = onSnapshot(
      allSongsQuery,
      (snapshot) => {
        console.log('songCounts snapshot received:', snapshot.size, 'docs');
        const songs = snapshot.docs.map((doc) => ({
          slug: doc.id,
          ...doc.data(),
        }));
        setTopSongs(songs);
      },
      (err) => {
        console.error('Error listening to songCounts:', err);
        // Don't set error state here - just log it
      }
    );

    return () => unsubscribe();
  }, []);

  // Seed songCounts (admin action)
  const handleSeedSongCounts = async () => {
    setSeeding(true);
    setSeedError(null);
    try {
      const seedSongCounts = httpsCallable(functions, 'seedSongCounts');
      console.log('Calling seedSongCounts function...');
      const result = await seedSongCounts();
      console.log('Seed result:', result);
      alert(`Successfully seeded ${result.data.totalSongs} songs!`);
    } catch (err) {
      console.error('Error seeding songCounts:', err);
      setSeedError(err.message || 'Failed to seed song counts');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return <div className="admin-metrics">Loading metrics...</div>;
  }

  if (error) {
    return <div className="admin-metrics error">Error: {error}</div>;
  }

  return (
    <div className="admin-metrics">
      <h2>Admin Metrics</h2>
      
      <div className="metric-card">
        <h3>Total User Selections</h3>
        <div className="metric-value">{totalSelections}</div>
      </div>

      <div className="metric-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>All Songs - Vote Counts ({topSongs.length} songs)</h3>
          <button 
            onClick={handleSeedSongCounts}
            disabled={seeding}
            className="seed-button"
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              backgroundColor: '#0FA52A',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: seeding ? 'not-allowed' : 'pointer',
              opacity: seeding ? 0.6 : 1,
            }}
          >
            {seeding ? 'Seeding...' : 'Seed Counts'}
          </button>
        </div>
        
        {seedError && (
          <div className="seed-error" style={{ color: '#ff4444', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
            {seedError}
          </div>
        )}

        {topSongs.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
            <p>No song counts yet</p>
            <p style={{ fontSize: '0.9rem' }}>Click "Seed Counts" to initialize from existing selections</p>
          </div>
        ) : (
          <div className="top-songs-list" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {topSongs.map((song, index) => (
              <div 
                key={song.slug} 
                className="top-song-item"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  borderBottom: '1px solid #333',
                  backgroundColor: index < 3 ? 'rgba(254, 218, 45, 0.1)' : 'transparent',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ 
                    fontWeight: 'bold', 
                    color: index < 3 ? '#feda2d' : '#999',
                    minWidth: '2.5rem',
                  }}>
                    #{index + 1}
                  </span>
                  <span style={{ textTransform: 'uppercase' }}>
                    {song.title || song.slug.replace(/-/g, ' ')}
                  </span>
                </span>
                <span style={{ fontWeight: 'bold', color: '#feda2d' }}>
                  {song.count} {song.count === 1 ? 'vote' : 'votes'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMetrics;